import { Boxes, FolderOpen, LoaderCircle, LogOut, Plus, Trash2 } from "lucide-react";
import { type FormEvent, useCallback, useEffect, useState } from "react";
import { useAuth } from "../auth/auth-context";
import { createProject, deleteProject, listProjects } from "./project-api";
import type { ProjectSummary } from "./project-types";

type ProjectListPageProps = {
  onManageDevices: () => void;
  onOpenProject: (projectId: string) => void;
};

export function ProjectListPage({ onManageDevices, onOpenProject }: ProjectListPageProps) {
  const { logout } = useAuth();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [name, setName] = useState("");
  const [projects, setProjects] = useState<ProjectSummary[]>([]);
  const [status, setStatus] = useState<"error" | "loading" | "ready">("loading");

  const loadProjectList = useCallback(async () => {
    setStatus("loading");
    setError(undefined);
    try {
      setProjects(await listProjects());
      setStatus("ready");
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "读取项目列表失败");
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    void loadProjectList();
  }, [loadProjectList]);

  const handleCreate = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(undefined);
    try {
      const project = await createProject(name);
      setName("");
      onOpenProject(project.id);
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : "创建项目失败");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async (project: ProjectSummary) => {
    if (!window.confirm(`确定删除项目“${project.name}”吗？项目图纸和全部设置都会永久删除。`)) {
      return;
    }
    setBusy(true);
    setError(undefined);
    try {
      await deleteProject(project.id, project.version);
      setProjects((current) => current.filter((item) => item.id !== project.id));
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : "删除项目失败");
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="project-list-page">
      <header className="project-list-header">
        <div>
          <span>HOLO CARGO</span>
          <h1>场景项目</h1>
        </div>
        <div className="project-list-actions">
          <button onClick={onManageDevices} type="button">
            <Boxes aria-hidden="true" size={17} />
            设备模型
          </button>
          <button onClick={() => void logout()} type="button">
            <LogOut aria-hidden="true" size={17} />
            退出登录
          </button>
        </div>
      </header>

      <section className="project-list-workspace">
        <form className="project-create-form" onSubmit={(event) => void handleCreate(event)}>
          <div>
            <h2>新建项目</h2>
            <p>先创建项目，再上传该项目唯一的 DXF 图纸。</p>
          </div>
          <label>
            <span>项目名称</span>
            <input
              disabled={busy}
              maxLength={80}
              onChange={(event) => setName(event.target.value)}
              placeholder="例如：一号仓库布局"
              value={name}
            />
          </label>
          <button disabled={busy || !name.trim()} type="submit">
            {busy ? (
              <LoaderCircle aria-hidden="true" size={18} />
            ) : (
              <Plus aria-hidden="true" size={18} />
            )}
            创建项目
          </button>
        </form>

        <section className="project-list" aria-label="项目列表">
          <div className="project-list-title">
            <h2>全部项目</h2>
            <span>{projects.length} 个</span>
          </div>
          {status === "ready" && projects.length > 0 ? (
            <div aria-hidden="true" className="project-list-columns">
              <span>项目 / 图纸</span>
              <span>最后更新</span>
              <span>操作</span>
            </div>
          ) : null}
          {status === "loading" ? (
            <div className="project-list-message">
              <LoaderCircle aria-hidden="true" size={20} />
              正在读取项目...
            </div>
          ) : null}
          {status === "error" ? (
            <div className="project-list-message is-error">
              <span>{error}</span>
              <button onClick={() => void loadProjectList()} type="button">
                重试
              </button>
            </div>
          ) : null}
          {status === "ready" && projects.length === 0 ? (
            <div className="project-list-message">还没有项目，请先在左侧创建。</div>
          ) : null}
          {projects.map((project) => (
            <article className="project-list-item" key={project.id}>
              <button
                className="project-list-open"
                onClick={() => onOpenProject(project.id)}
                type="button"
              >
                <span className="project-list-item-icon">
                  <FolderOpen aria-hidden="true" size={20} />
                </span>
                <span>
                  <strong>{project.name}</strong>
                  <small>{project.drawingFileName ?? "尚未上传图纸"}</small>
                </span>
              </button>
              <time dateTime={project.updatedAt}>{formatDate(project.updatedAt)}</time>
              <button
                aria-label={`删除 ${project.name}`}
                className="project-list-delete"
                disabled={busy}
                onClick={() => void handleDelete(project)}
                title="删除项目"
                type="button"
              >
                <Trash2 aria-hidden="true" size={16} />
              </button>
            </article>
          ))}
          {status !== "error" && error ? <p className="project-list-error">{error}</p> : null}
        </section>
      </section>
    </main>
  );
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat("zh-CN", {
    dateStyle: "medium",
    timeStyle: "short"
  }).format(new Date(value));
}
